import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Card, CardContent, Typography, Chip, Divider,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  RadioGroup, FormControlLabel, Radio, Snackbar, Alert,
  IconButton, Skeleton,
} from "@mui/material";
import ArrowBackRoundedIcon      from "@mui/icons-material/ArrowBackRounded";
import LocalShippingRoundedIcon  from "@mui/icons-material/LocalShippingRounded";
import PhoneRoundedIcon          from "@mui/icons-material/PhoneRounded";
import PersonRoundedIcon         from "@mui/icons-material/PersonRounded";
import BadgeRoundedIcon          from "@mui/icons-material/BadgeRounded";
import ManageAccountsRoundedIcon from "@mui/icons-material/ManageAccountsRounded";
import Inventory2RoundedIcon     from "@mui/icons-material/Inventory2Rounded";
import CurrencyRupeeRoundedIcon  from "@mui/icons-material/CurrencyRupeeRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import ReceiptLongRoundedIcon    from "@mui/icons-material/ReceiptLongRounded";
import StraightRoundedIcon       from "@mui/icons-material/StraightRounded";
import { decryptId, encryptId } from "../helper/idCodec.js"; // eslint-disable-line no-unused-vars
import { API_ENDPOINTS, getData, postData } from "../../services/apiService.js";
import Form from "react-bootstrap/Form";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
  teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
  orange:  "#FF7900", orangeLt: "#FFF0E0",
  emerald: "#059669", emeraldLt: "#D1FAE5",
  rose:    "#E11D48", roseLt:   "#FFE4E6",
  amber:   "#D97706", amberLt:  "#FEF3C7",
  indigo:  "#4F46E5", indigoLt: "#EEF2FF",
  bg:      "#F7FAFC", border: "#E2E8F0",
  text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtRs  = n => n != null ? `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";
const fmtDate= d => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

// ── Section card wrapper ──────────────────────────────────────────────────
const SCard = ({ children, sx = {} }) => (
  <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", ...sx }}>
    {children}
  </Card>
);

const SHead = ({ title, action }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
    <Typography sx={{ fontSize: 14, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</Typography>
    {action}
  </Box>
);

// ── Info row ──────────────────────────────────────────────────────────────
const InfoRow = ({ icon, label, value }) => (
  <Box sx={{ display: "flex", gap: 1.5, py: 1.2, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" } }}>
    <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: T.tealLt, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { fontSize: 15, color: T.teal } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, mt: 0.1 }}>{value || "—"}</Typography>
    </Box>
  </Box>
);

// ── KPI tile ──────────────────────────────────────────────────────────────
const KpiTile = ({ label, value, icon, bg, color, onClick, sub }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: "1 1 120px", p: 1.8, borderRadius: "12px", bgcolor: bg,
      border: `1px solid ${color}22`, cursor: onClick ? "pointer" : "default",
      transition: "transform 0.15s", "&:hover": onClick ? { transform: "translateY(-1px)", filter: "brightness(0.97)" } : {},
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.4px", mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 10, color, opacity: 0.75, mt: 0.3 }}>{sub}</Typography>}
      </Box>
      {React.cloneElement(icon, { sx: { fontSize: 18, color, opacity: 0.7 } })}
    </Box>
  </Box>
);

// ── Payment method card ───────────────────────────────────────────────────
const PayCard = ({ title, data, accent }) => {
  const amount = Number(data?.amount) || 0;
  return (
    <Box sx={{ flex: "1 1 160px", p: 2, borderRadius: "12px", bgcolor: T.bg, border: `1px solid ${T.border}`, borderLeft: `3px solid ${accent}` }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.text, mb: 1.5, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{title}</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
        <Typography sx={{ fontSize: 11, color: T.muted }}>Orders</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.text }}>{data?.packages ?? 0}</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: 11, color: T.muted }}>Amount</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: accent }}>{fmtRs(amount)}</Typography>
      </Box>
    </Box>
  );
};

// ── Payout breakdown row ──────────────────────────────────────────────────
const PayoutRow = ({ label, value, color, bold }) => (
  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: `1px solid ${T.border}`, "&:last-child": { borderBottom: "none" } }}>
    <Typography sx={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: T.text }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 700, color: color || T.text }}>{fmtRs(value)}</Typography>
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────
const TripDetails = () => {
  const { id } = useParams();
  const tripId   = id ? decryptId(id) : "";
  const navigate = useNavigate();

  const [trip,            setTrip]            = useState(null);
  const [paymentsSummary, setPaymentsSummary] = useState(null);
  const [packageInfo,     setPackageInfo]     = useState(null);
  const [adminPkgCounts,  setAdminPkgCounts]  = useState(null); // accurate counts from admin endpoint
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);

  const [openPaymentDialog,  setOpenPaymentDialog]  = useState(false);
  const [paymentMethod,      setPaymentMethod]      = useState("wallet");
  const [paymentAmount,      setPaymentAmount]      = useState("");
  const [snackbarOpen,       setSnackbarOpen]       = useState(false);
  const [snackbarMessage,    setSnackbarMessage]    = useState("");
  const [resultDialog,       setResultDialog]       = useState({ open: false, success: null });

  const fetchTrip = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Fetch trip details + accurate package counts in parallel
      const [tripRes, pkgRes] = await Promise.allSettled([
        getData(API_ENDPOINTS.fetchTripById, { tripId }),
        getData(API_ENDPOINTS.adminTripPackages, { trip_id: tripId, page: 1, limit: 1 }),
      ]);
      if (tripRes.status === "fulfilled") {
        setTrip(tripRes.value.result.trip);
        setPaymentsSummary(tripRes.value.result.payments_summary);
        setPackageInfo(tripRes.value.result.package_info);
      } else {
        throw new Error("Trip fetch failed");
      }
      if (pkgRes.status === "fulfilled" && pkgRes.value?.result?.statusCounts) {
        setAdminPkgCounts(pkgRes.value.result.statusCounts);
      }
    } catch (_) { setError("Unable to fetch trip details."); }
    finally     { setLoading(false); }
  }, [tripId]);

  useEffect(() => { if (tripId) fetchTrip(); }, [tripId, fetchTrip]);

  const handleOpenPaymentDialog = () => {
    const suggested = Number(paymentsSummary?.owner_owed) || 0;
    setPaymentAmount(suggested > 0 ? suggested.toFixed(2) : "");
    setOpenPaymentDialog(true);
  };

  const handleConfirmPayment = async () => {
    if (!trip) return;
    if (!paymentAmount || Number(paymentAmount) <= 0) {
      setSnackbarMessage("Please enter a valid payout amount."); setSnackbarOpen(true); return;
    }
    try {
      const response = await postData(API_ENDPOINTS.createOwnerPayment, {
        trip_id:        trip.id,
        wallet_id:      trip.owner_wallet_id,
        total_amount:   Number(paymentsSummary?.finalAmount) || 0,
        payout_amount:  Number(paymentAmount),
        payment_method: paymentMethod,
      });
      setResultDialog({ open: true, success: response?.statusCode === 200 });
      setOpenPaymentDialog(false);
    } catch (err) {
      const status = err?.response?.status;
      const data   = err?.response?.data;
      if (status === 409 && data?.message) {
        let detail = data.message;
        const blockers = data?.result?.blockers;
        if (Array.isArray(blockers) && blockers.length > 0) {
          detail += ` Open: ${blockers.map(b => `${b.type === "complaint" ? "Complaint" : "Claim"} ${b.ref || `#${b.id}`} (${b.status})`).join(", ")}.`;
        }
        setSnackbarMessage(detail); setSnackbarOpen(true); setOpenPaymentDialog(false); return;
      }
      if (status === 403) {
        setSnackbarMessage(data?.message || "Admin access required."); setSnackbarOpen(true); setOpenPaymentDialog(false); return;
      }
      setResultDialog({ open: true, success: false }); setOpenPaymentDialog(false);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────
  if (loading) return (
    <Box sx={{ bgcolor: T.bg, minHeight: "100vh", p: 3 }}>
      <Skeleton variant="rounded" height={120} sx={{ borderRadius: "20px", mb: 3 }} />
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        {[1,2,3].map(i => <Skeleton key={i} variant="rounded" height={200} sx={{ flex: 1, borderRadius: "16px" }} />)}
      </Box>
      <Skeleton variant="rounded" height={180} sx={{ borderRadius: "16px" }} />
    </Box>
  );
  if (error)  return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  if (!trip)  return <Typography p={3} color="text.secondary">No trip data found.</Typography>;

  // ── Derived values ────────────────────────────────────────────────────
  const route    = trip.TripStops?.map(s => s.city_name).join(" → ") || "Route not available";
  const owner    = trip.Truck?.Owner ? `${trip.Truck.Owner.first_name || ""} ${trip.Truck.Owner.last_name || ""}`.trim() : "N/A";
  const driver   = trip.Drivers     ? `${trip.Drivers.first_name || ""} ${trip.Drivers.last_name || ""}`.trim() : "N/A";
  const ownerPhone  = trip.Truck?.Owner?.mobile_num || "N/A";
  const driverPhone = trip.Drivers?.mobile_num || "N/A";

  const totalBalance        = (paymentsSummary?.cod.amount || 0) + (paymentsSummary?.wallet.amount || 0) + (paymentsSummary?.online.amount || 0);
  const platformFee         = paymentsSummary?.platform.amount || 0;
  const outstandingAmount   = Number(paymentsSummary?.outstanding) || 0;
  const ownerSubtotalDelivered = Number(paymentsSummary?.owner_subtotal_delivered) || 0;
  const ownerAlreadyPaid    = Number(paymentsSummary?.owner_already_paid) || 0;
  const ownerOwed           = Number(paymentsSummary?.owner_owed) || 0;
  const dimIncentiveCredited= Number(paymentsSummary?.dim_incentive_credited) || 0;
  const finalAmount         = Number(paymentsSummary?.finalAmount) || 0;

  const isCompleted = trip.trip_started && trip.trip_completed;
  const isActive    = trip.trip_started && !trip.trip_completed;
  const statusCfg   = isCompleted
    ? { label: "Completed",  bg: T.tealLt,   color: T.teal   }
    : isActive
    ? { label: "Active",     bg: T.emeraldLt,color: T.emerald }
    : { label: "Not Started",bg: T.amberLt,  color: T.amber  };

  return (
    <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

      {/* ── Hero header ── */}
      <Box sx={{
        background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
        borderRadius: "20px", p: 3, mb: 3,
        boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
      }}>
        {/* Breadcrumb */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <IconButton onClick={() => navigate("/app/trips")}
            sx={{ bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "10px", color: "#fff", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}>
            <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 600 }}>Trips</Typography>
        </Box>

        {/* Route + meta */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.8 }}>
              <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <LocalShippingRoundedIcon sx={{ fontSize: 22, color: "#fff" }} />
              </Box>
              <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif", lineHeight: 1.1 }}>
                {route}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.6, bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px", px: 1.2, py: 0.3 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>Trip #{trip.id}</Typography>
              </Box>
              {trip.trip_in_kms && (
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                  <StraightRoundedIcon sx={{ fontSize: 13, mr: 0.3, verticalAlign: "text-bottom" }} />
                  {trip.trip_in_kms} km
                </Typography>
              )}
              {trip.actual_start_time && (
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                  Started {fmtDate(trip.actual_start_time)}
                </Typography>
              )}
              {trip.reach_date && (
                <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.75)" }}>
                  → {fmtDate(trip.reach_date)}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Status + engine chips */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {trip.pricing_engine_version != null && (
              <Chip
                size="small"
                label={`Engine v${trip.pricing_engine_version}`}
                sx={{ fontWeight: 600, fontSize: 11, height: 22, borderRadius: "7px", bgcolor: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)" }}
              />
            )}
            <Chip
              label={statusCfg.label}
              sx={{ fontWeight: 700, fontSize: 12, height: 26, borderRadius: "9px", bgcolor: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.color}33` }}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Info cards row ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 2, mb: 3 }}>

        {/* Owner & truck */}
        <SCard>
          <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <SHead title="Owner & Truck" />
            <InfoRow icon={<ManageAccountsRoundedIcon />} label="Truck Owner"    value={owner} />
            <InfoRow icon={<PhoneRoundedIcon />}           label="Mobile"         value={ownerPhone} />
            <InfoRow icon={<LocalShippingRoundedIcon />}   label="Truck Name"     value={trip.Truck?.truck_name} />
            <InfoRow icon={<BadgeRoundedIcon />}           label="Truck Number"   value={trip.Truck?.truck_no} />
          </CardContent>
        </SCard>

        {/* Driver */}
        <SCard>
          <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <SHead title="Driver" />
            <InfoRow icon={<PersonRoundedIcon />} label="Driver Name"    value={driver} />
            <InfoRow icon={<PhoneRoundedIcon />}  label="Mobile"         value={driverPhone} />
            <InfoRow icon={<BadgeRoundedIcon />}  label="License Number" value={trip.Drivers?.license_no} />
          </CardContent>
        </SCard>

        {/* Package info */}
        {(packageInfo || adminPkgCounts) && (() => {
          // When adminPkgCounts is available use it exclusively (missing keys → 0).
          // Only fall back to packageInfo when the admin fetch hasn't completed yet.
          const c = adminPkgCounts;
          const total     = c != null ? (c.All       ?? 0) : (packageInfo?.total_packages     ?? 0);
          const delivered = c != null ? (c.Delivered ?? 0) : (packageInfo?.delivered_packages ?? 0);
          const picked    = c != null ? (c.Picked    ?? 0) : (packageInfo?.ongoing_packages   ?? 0);
          const accepted  = c != null ? (c.Accepted  ?? 0) : (packageInfo?.accepted_packages  ?? 0);
          const archived  = c != null ? (c.Archived  ?? 0) : 0;

          return (
            <SCard>
              <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
                <SHead title="Package Summary" />
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 0.5 }}>
                  <KpiTile label="Total"     value={total}     icon={<Inventory2RoundedIcon />}    bg={T.roseLt}    color={T.rose}    onClick={() => navigate(`/app/trip-packages/${encryptId(tripId)}`)} />
                  <KpiTile label="Delivered" value={delivered} icon={<Inventory2RoundedIcon />}    bg={T.emeraldLt} color={T.emerald} onClick={() => navigate(`/app/trip-packages/${encryptId(tripId)}?status=Delivered`)} />
                  <KpiTile label="Picked"    value={picked}    icon={<LocalShippingRoundedIcon />} bg={T.indigoLt}  color={T.indigo}  onClick={() => navigate(`/app/trip-packages/${encryptId(tripId)}?status=Picked`)} />
                  <KpiTile label="Accepted"  value={accepted}  icon={<Inventory2RoundedIcon />}    bg={T.amberLt}   color={T.amber}   onClick={() => navigate(`/app/trip-packages/${encryptId(tripId)}?status=Accepted`)} />
                  {archived > 0 && (
                    <KpiTile label="Archived" value={archived} icon={<Inventory2RoundedIcon />}   bg="#F1F5F9"     color={T.muted}   onClick={() => navigate(`/app/trip-packages/${encryptId(tripId)}?status=Archived`)} />
                  )}
                </Box>
                <Typography sx={{ fontSize: 10, color: T.muted, mt: 1.5, textAlign: "center" }}>Click a tile to view packages</Typography>
              </CardContent>
            </SCard>
          );
        })()}
      </Box>

      {/* ── Payment section ── */}
      {paymentsSummary && (
        <SCard sx={{ mb: 3 }}>
          <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
            <SHead
              title="Payment Summary"
              action={
                <Box sx={{ px: 2, py: 0.6, bgcolor: T.tealLt, borderRadius: "10px", border: `1px solid ${T.teal}22` }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: T.teal, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                    Total Received {fmtRs(totalBalance)}
                  </Typography>
                </Box>
              }
            />

            {/* Revenue KPI row */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
              <KpiTile label="Total Received"  value={fmtRs(totalBalance)}       icon={<CurrencyRupeeRoundedIcon />}         bg={T.tealLt}    color={T.teal}   />
              <KpiTile label="Platform Fee"    value={fmtRs(platformFee)}        icon={<ReceiptLongRoundedIcon />}           bg={T.indigoLt}  color={T.indigo} />
              <KpiTile label="Outstanding"     value={fmtRs(outstandingAmount)}  icon={<AccountBalanceWalletRoundedIcon />}  bg={outstandingAmount > 0 ? T.roseLt : T.emeraldLt} color={outstandingAmount > 0 ? T.rose : T.emerald} />
              <KpiTile label="Owner Owed"      value={fmtRs(ownerOwed)}          icon={<AccountBalanceWalletRoundedIcon />}  bg={ownerOwed > 0 ? T.orangeLt : T.emeraldLt} color={ownerOwed > 0 ? T.orange : T.emerald} sub={ownerOwed > 0 ? "unpaid" : "settled"} />
            </Box>

            {/* Payment method breakdown */}
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", mb: 1.5 }}>By payment method</Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2.5 }}>
              <PayCard title="Cash on Delivery"  data={paymentsSummary.cod}      accent={T.amber}   />
              <PayCard title="Online Payment"    data={paymentsSummary.online}   accent={T.indigo}  />
              <PayCard title="Wallet Payment"    data={paymentsSummary.wallet}   accent={T.teal}    />
              <PayCard title="Platform Revenue"  data={paymentsSummary.platform} accent={T.emerald} />
            </Box>

            {/* Owner payout section */}
            <Box sx={{ p: 2, bgcolor: T.bg, borderRadius: "12px", border: `1px solid ${T.border}` }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                  Owner Payout
                </Typography>
                {ownerOwed > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<AccountBalanceWalletRoundedIcon />}
                    onClick={handleOpenPaymentDialog}
                    disableElevation
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, fontSize: 13, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk } }}
                  >
                    Initiate Payout
                  </Button>
                )}
              </Box>
              <PayoutRow label="Owner subtotal (delivered)" value={ownerSubtotalDelivered} />
              <PayoutRow label="Already paid out"           value={ownerAlreadyPaid} color={T.muted} />
              <PayoutRow label="Owed to owner"              value={ownerOwed} color={ownerOwed > 0 ? T.orange : T.emerald} bold />

              {ownerAlreadyPaid > ownerSubtotalDelivered && (
                <Box sx={{ mt: 1.5, p: 1.2, bgcolor: T.roseLt, borderRadius: "8px", border: `1px solid ${T.rose}33` }}>
                  <Typography sx={{ fontSize: 12, color: T.rose, fontWeight: 600 }}>
                    Over-credited by {fmtRs(ownerAlreadyPaid - ownerSubtotalDelivered)} — no further payout required.
                  </Typography>
                </Box>
              )}
              {ownerOwed === 0 && ownerAlreadyPaid <= ownerSubtotalDelivered && (
                <Box sx={{ mt: 1.5, p: 1.2, bgcolor: T.emeraldLt, borderRadius: "8px", border: `1px solid ${T.emerald}33` }}>
                  <Typography sx={{ fontSize: 12, color: T.emerald, fontWeight: 600 }}>
                    Fully settled. {ownerAlreadyPaid > 0 ? `Paid ${fmtRs(ownerAlreadyPaid)}.` : ""}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </SCard>
      )}

      {/* ── Payout dialog ── */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: "20px" } }}>
        <DialogTitle sx={{ fontWeight: 800, fontSize: 17, fontFamily: "'Plus Jakarta Sans', sans-serif", pb: 1 }}>
          Initiate Owner Payout
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Typography sx={{ fontSize: 13, color: T.muted, mb: 0.5 }}>Breakdown for Trip #{trip.id}</Typography>
            <PayoutRow label="Owner subtotal (delivered)" value={ownerSubtotalDelivered} />
            <PayoutRow label="Already paid out"           value={ownerAlreadyPaid} color={T.muted} />
            <PayoutRow label="Owed"                       value={ownerOwed} color={ownerOwed > 0 ? T.orange : T.emerald} bold />
            {dimIncentiveCredited > 0 && (
              <Alert severity="warning" sx={{ py: 0.5, borderRadius: "10px" }}>
                <strong>{fmtRs(dimIncentiveCredited)}</strong> in DIM incentives already auto-credited — do not add again.
              </Alert>
            )}
            <Divider sx={{ my: 0.5 }} />
            <PayoutRow label="Total amount received" value={totalBalance} />
            <PayoutRow label="Platform fee"          value={platformFee} color={T.rose} />
            <PayoutRow label="Trip net"              value={finalAmount} color={T.muted} />

            <Box sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, mb: 0.8 }}>
                Payout amount <Typography component="span" sx={{ fontSize: 11, color: T.muted }}>(suggested {fmtRs(ownerOwed)})</Typography>
              </Typography>
              <Form.Control
                type="number" placeholder="Enter amount"
                value={paymentAmount}
                onChange={e => setPaymentAmount(e.target.value)}
                style={{ borderRadius: 10, border: `1.5px solid ${T.border}`, padding: "8px 12px", fontSize: 14 }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text, mb: 0.5 }}>Payment method</Typography>
              <RadioGroup value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)}>
                <FormControlLabel value="wallet" control={<Radio size="small" />} label="Digital Wallet" sx={{ "& .MuiFormControlLabel-label": { fontSize: 13 } }} />
              </RadioGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setOpenPaymentDialog(false)} variant="outlined" sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" disableElevation onClick={handleConfirmPayment}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk } }}>
            Confirm Payout
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Result dialog ── */}
      <Dialog open={resultDialog.open} onClose={() => setResultDialog({ open: false, success: null })}
        PaperProps={{ sx: { borderRadius: "20px", textAlign: "center" } }}>
        <DialogContent sx={{ p: 5 }}>
          {resultDialog.success
            ? <><img src="/smart-vectors/Payment Successful.svg" alt="success" style={{ width: 80, marginBottom: 12 }} /><Typography variant="h6" color="success.main" fontWeight={700}>Payout Successful!</Typography></>
            : <><img src="/smart-vectors/Payment Failed.svg"     alt="failure" style={{ width: 80, marginBottom: 12 }} /><Typography variant="h6" color="error"           fontWeight={700}>Payout Failed</Typography></>
          }
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3, gap: 1 }}>
          <Button variant="contained" disableElevation color={resultDialog.success ? "success" : "error"}
            sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
            onClick={async () => { setResultDialog({ open: false, success: null }); await fetchTrip(); }}>
            OK
          </Button>
          {!resultDialog.success && (
            <Button variant="outlined" sx={{ borderRadius: "10px", textTransform: "none" }}
              onClick={() => setResultDialog({ open: false, success: null })}>Close</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={() => setSnackbarOpen(false)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" variant="filled" sx={{ borderRadius: "12px" }}>{snackbarMessage}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TripDetails;
